import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface OpenPrn {
  id: string;
  prnNumber: string;
  status: string;
  returnDate: string;
  totalQty: number;
  totalAmount: number;
  itemCount: number;
  storeCode: string | null;
  remarks: string | null;
}

export interface OpenPrnBucket {
  count: number;
  totalValue: number;
  prns: OpenPrn[];
}

/**
 * Vendor returns split by lifecycle stage:
 * awaitingHandover = POSTED (stock debited, goods staged for pickup);
 * inPreparation = DRAFT/PENDING/APPROVED (paperwork only).
 */
export interface OpenPrnSummary {
  vendorId: string;
  awaitingHandover: OpenPrnBucket;
  inPreparation: OpenPrnBucket;
}

@Injectable({
  providedIn: 'root',
})
export class PrnService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Create PRN
  createPrn(body: any) {
    return this.http.post(`${this.baseUrl}/prn`, body);
  }

  // Get paginated list of PRNs
  getPrns(
    pageOptions: { page: number; limit: number },
    searchText: string = ''
  ) {
    let params = new HttpParams()
      .set('page', pageOptions.page)
      .set('limit', pageOptions.limit);

    if (searchText) {
      params = params.set('search', searchText);
    }

    return this.http.get(`${this.baseUrl}/prn`, { params });
  }

  // Get PRN by ID
  getPrnById(id: string) {
    return this.http.get(`${this.baseUrl}/prn/${id}`);
  }

  /** Vendor returns awaiting handover (POSTED) plus those still in preparation. */
  getOpenPrnsByVendor(vendorId: string) {
    return this.http.get<OpenPrnSummary>(
      `${this.baseUrl}/prn/open-by-vendor/${vendorId}`
    );
  }

  /** POSTED -> DISPATCHED: goods physically handed to the vendor. */
  dispatchPrn(id: string, remarks?: string) {
    return this.http.patch(`${this.baseUrl}/prn/${id}/dispatch`, {
      remarks: remarks || undefined,
    });
  }

  // Update PRN by ID
  updatePrn(id: string, body: any) {
    return this.http.patch(`${this.baseUrl}/prn/${id}`, body);
  }

  // Download PRN as PDF
  downloadPrn(prnId: string) {
    return this.http.get(`${this.baseUrl}/prn/download/${prnId}`, {
      responseType: 'blob' as 'blob',
      observe: 'response' as 'response',
    });
  }

  // Get next PRN number
  getNextPrnNumber(storeId?: string) {
    const params: any = {};
    if (storeId) params.storeId = storeId;
    return this.http.get(`${this.baseUrl}/prn/next-number`, { params });
  }

  /**
   * Excel template (S.No, SKU, Qty). With a vendor it comes pre-filled with
   * that vendor's SKUs so the user only types quantities.
   */
  downloadTemplate(vendorId?: string) {
    const params = vendorId
      ? new HttpParams().set('vendorId', vendorId)
      : new HttpParams();
    return this.http.get(`${this.baseUrl}/prn/template`, {
      responseType: 'blob',
      params,
    });
  }

  /** Parse a filled template into preview rows. Creates nothing. */
  parseUpload(file: File, vendorId: string) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(
      `${this.baseUrl}/prn/parse-upload?vendorId=${encodeURIComponent(vendorId)}`,
      formData,
    );
  }
}
